/**
 * BuildStamp — the footer "which build is this?" marker.
 *
 * Shows the deploy environment (a subtle chip, only for non-production so prod
 * stays uncluttered), the app version, and the short commit SHA linking to the
 * exact commit on GitHub. Makes deploys legible: watch the SHA change on staging
 * while prod holds, and confirm a redeploy actually landed.
 *
 * Split into a pure `BuildStampView` (prop-driven, fully testable) and the
 * connected `BuildStamp` that reads the runtime host + build-time env — mirroring
 * the CollectorStatus split. When nothing is baked in (local dev) it degrades to
 * a plain "build locale" marker with no dead link.
 */

import { type ReactNode } from "react";
import { buildInfo, type BuildInfo } from "@/lib/build";

/** Human label for the env chip; production is intentionally unlabelled. */
const ENV_LABEL: Record<BuildInfo["env"], string> = {
  production: "",
  staging: "staging",
  dev: "dev",
};

interface BuildStampViewProps {
  info: BuildInfo;
}

export function BuildStampView({ info }: BuildStampViewProps) {
  const envLabel = ENV_LABEL[info.env];

  const parts: ReactNode[] = [];
  if (envLabel) {
    parts.push(
      <span key="env" className={`footer-build__env footer-build__env--${info.env}`}>
        {envLabel}
      </span>,
    );
  }
  if (info.version) {
    parts.push(<span key="version">v{info.version}</span>);
  }
  if (info.shaShort && info.commitUrl) {
    parts.push(
      <a
        key="sha"
        className="footer-build__sha"
        href={info.commitUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        {info.shaShort}
      </a>,
    );
  }
  if (parts.length === 0) {
    parts.push(<span key="local">build locale</span>);
  }

  // Accessible one-liner naming the whole stamp, so the middot-separated bits
  // don't read as disconnected tokens to a screen reader.
  const label = [
    info.env !== "production" ? info.env : null,
    info.version ? `v${info.version}` : null,
    info.shaShort ? `commit ${info.shaShort}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    // role="group" (not the implicit <p>/paragraph role, which is name-prohibited)
    // so the assembled accessible name is actually exposed rather than dropped —
    // otherwise AT reads the middot-separated bits as disconnected tokens.
    <div
      className="footer-build"
      role="group"
      aria-label={label ? `Build: ${label}` : "Build locale"}
    >
      {parts.map((part, i) => (
        <span key={i} className="footer-build__item">
          {i > 0 && <span aria-hidden="true"> · </span>}
          {part}
        </span>
      ))}
    </div>
  );
}

export function BuildStamp() {
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const info = buildInfo(host, import.meta.env.VITE_APP_VERSION, import.meta.env.VITE_GIT_SHA);
  return <BuildStampView info={info} />;
}
